import { useState } from "react";

import { Select, Table, Tabs } from "antd";
import { ColumnsType } from "antd/es/table";
import styled from "styled-components";

import useCanUseGeoModels from "hooks/useCanUseGeoModels";
import useOrgUsersQuery from "hooks/useOrgUsersQuery";

import { IpdbSourcePlay } from "api/ipdp";

import { Heading } from "../base";
import useGeoModelAccessQuery from "./hooks/useGeoModelAccessQuery";
import { useUpdateMapLayerGroupAccess } from "./hooks/useMapLayerGroupAccessMutations";
import useMapLayerGroupsQuery from "./hooks/useMapLayerGroupsQuery";
import type { MapLayerAccess } from "./hooks/useMapLayerGroupsQuery";
import { useUpdateSourceAccess } from "./hooks/useSourceAccessMutations";

const { TabPane } = Tabs;

export function GeoModelAccess() {
  const [activeTab, setActiveTab] = useState("3d-models");

  const { userOptions, isLoading: isLoadingUsers } = useOrgUsersQuery();
  const { geoModels } = useGeoModelAccessQuery();
  const { mapLayerGroups } = useMapLayerGroupsQuery();

  const canUseGeoModels = useCanUseGeoModels();

  const updateSourceAccessMutation = useUpdateSourceAccess();
  const updateMapLayerGroupAccessMutation = useUpdateMapLayerGroupAccess();

  if (!canUseGeoModels) return null;

  const handleSourceAccessChange = (source: string, userList: string[]) => {
    updateSourceAccessMutation.mutate({ source, userList });
  };

  const handleMapLayerAccessChange = (group: string, userList: string[]) => {
    updateMapLayerGroupAccessMutation.mutate({ group, userList });
  };

  const filterOption = (inputValue: string, option: { label: string }) => {
    if (inputValue && option.label) {
      return option.label.toLowerCase().includes(inputValue.toLowerCase());
    }
    return true;
  };

  const renderUserSelector = (
    userList: string[],
    isLoading: boolean,
    onChange: (value: string[]) => void
  ) => (
    <SelectWrapper
      mode="multiple"
      loading={isLoadingUsers || isLoading}
      popupClassName="modal-select"
      placeholder="All users"
      value={userList}
      maxTagCount={2}
      maxTagTextLength={12}
      onChange={onChange}
      filterOption={filterOption}
      options={userOptions}
    />
  );

  const threeDModelColumns: ColumnsType<IpdbSourcePlay> = [
    {
      title: "Source Name",
      dataIndex: "source",
      key: "source"
    },
    {
      title: "Shared With",
      dataIndex: "userList",
      key: "userList",
      width: 300,
      render: (_, record) =>
        renderUserSelector(
          record.userList || [],
          updateSourceAccessMutation.isLoading,
          (value) => handleSourceAccessChange(record.source, value)
        )
    }
  ];

  const mapLayerColumns: ColumnsType<MapLayerAccess> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Shared With",
      dataIndex: "userList",
      key: "userList",
      width: 300,
      render: (userList, record) =>
        renderUserSelector(
          userList,
          updateMapLayerGroupAccessMutation.isLoading,
          (value) => handleMapLayerAccessChange(record.group, value)
        )
    }
  ];
  return (
    <Container>
      <Heading element="h4">Geo Model Access</Heading>
      <StyledTabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="3D Model Access" key="3d-models">
          <Table
            dataSource={geoModels}
            columns={threeDModelColumns}
            rowKey="source"
            scroll={{ y: "calc(100vh - 280px)" }}
            pagination={{ pageSize: 50 }}
          />
        </TabPane>
        <TabPane tab="Map Layer Access" key="map-layers">
          <Table
            dataSource={mapLayerGroups}
            columns={mapLayerColumns}
            rowKey="group"
            scroll={{ y: "calc(100vh - 280px)" }}
            pagination={{ pageSize: 50 }}
          />
        </TabPane>
      </StyledTabs>
    </Container>
  );
}

const SelectWrapper = styled(Select)`
  width: 100%;
  max-width: 300px;

  .ant-select-selector {
    max-width: 300px;
    min-height: 32px !important;
    max-height: 32px !important;
    overflow: hidden;
  }

  .ant-select-selection-overflow {
    max-height: 32px !important;
    overflow: hidden;
    flex-wrap: nowrap !important;
  }

  .ant-select-selection-item {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 4px;
  }

  .ant-select-selection-search {
    max-height: 32px !important;
  }
`;

const Container = styled.div`
  height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
`;

const StyledTabs = styled(Tabs)`
  flex: 1;
  display: flex;
  flex-direction: column;

  .ant-tabs-content-holder {
    flex: 1;
    overflow: hidden;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;
